// Shared wrong-domain logic for the connectionpointapp.com forms domain.
//
// Two independent layers consume this module and they MUST agree:
//   1. middleware.js (Next.js Edge runtime) - the real block, runs before any
//      app code on every request the App Service serves.
//   2. src/index.js (React SPA bootstrap) - the fallback layer, for any request
//      that reaches the SPA without passing through the middleware.
//
// The path allowlist lives here precisely so the two layers cannot drift. A
// path allowed by one and blocked by the other would break public form
// rendering on whichever layer happened to serve the request, and those form
// URLs are already sitting in customers' text messages.

export const FORMS_DOMAIN = 'connectionpointapp.com';

export const DEFAULT_APP_BASE_URL = 'https://app.connectionpointsoftware.com';

export const INTERSTITIAL_PATH = '/moved';

// Public files at the site root that must keep serving on the forms domain.
const ALLOWED_EXACT_PATHS = [
  '/favicon.ico',
  '/favicon-new.ico',
  '/favicon-dss.ico',
  '/manifest.json',
  '/robots.txt',
  '/sitemap.xml',
  '/version.json',
  // Public mobile app download landing page. Consultants share this link out
  // of the Help screen and it carries no sign-in, so it stays reachable here.
  '/host',
  '/host/',
];

// Path prefixes that must keep serving on the forms domain.
const ALLOWED_PREFIXES = [
  '/f/', // the public Smart Form, the only reason this domain exists
  '/_next/', // every JS and CSS chunk the form page loads
  '/static/', // og:image and other public assets referenced by form metadata
];

// Files under /static that are the app shell itself rather than an asset.
// Serving these would hand the whole SPA to the user on the wrong domain.
const BLOCKED_STATIC_FILES = [
  '/static/index.html',
  '/static/ms-modal.html',
];

/**
 * True when the request host is any connectionpointapp.com host, including
 * www, qa, st, and the bare apex.
 *
 * Suffix match on the hostname with the port stripped, never a substring
 * check. app.connectionpointsoftware.com must not match, and neither must a
 * lookalike such as notconnectionpointapp.com.example.
 */
export function isFormsDomainHost(rawHost) {
  if (!rawHost) return false;
  const host = String(rawHost)
    .toLowerCase()
    .split(':')[0] // drop the port
    .replace(/\.$/, ''); // drop a trailing fully qualified dot
  return host === FORMS_DOMAIN || host.endsWith(`.${FORMS_DOMAIN}`);
}

/**
 * True when the path is public content that must keep working on the forms
 * domain. Everything else on that domain is app UI and gets blocked.
 */
export function isAllowedOnFormsDomain(rawPathname) {
  if (!rawPathname) return false;
  const pathname = String(rawPathname).toLowerCase();
  if (BLOCKED_STATIC_FILES.includes(pathname)) return false;
  if (ALLOWED_EXACT_PATHS.includes(pathname)) return true;
  return ALLOWED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Reduce an arbitrary continue path to something safe to append to the app
 * base URL. The middleware always supplies a real request path, but the
 * interstitial reads it back off the query string where anyone can craft it,
 * so this has to hold on its own.
 */
export function sanitizeContinuePath(rawPath) {
  if (typeof rawPath !== 'string' || rawPath.length === 0) return '/';
  // Site relative only. Reject protocol relative (//evil.example) and the
  // backslash variants some browsers normalize into it.
  if (!rawPath.startsWith('/')) return '/';
  if (rawPath.startsWith('//') || rawPath.startsWith('/\\')) return '/';
  // Never bounce the user back into the interstitial itself.
  if (rawPath === INTERSTITIAL_PATH || rawPath.startsWith(`${INTERSTITIAL_PATH}?`)) return '/';
  return rawPath;
}

/**
 * Build the continue link, preserving the path and query the user originally
 * asked for so connectionpointapp.com/contacts?foo=1 continues to
 * app.connectionpointsoftware.com/contacts?foo=1.
 */
export function buildContinueUrl(rawBaseUrl, rawPath) {
  const base = String(rawBaseUrl || DEFAULT_APP_BASE_URL).replace(/\/+$/, '');
  return `${base}${sanitizeContinuePath(rawPath)}`;
}

/**
 * The bare hostname to show the user, e.g. app.connectionpointsoftware.com.
 * This is the address they are meant to memorize, so it is displayed without
 * the scheme or a trailing slash.
 */
export function displayHostFromUrl(rawBaseUrl) {
  return String(rawBaseUrl || DEFAULT_APP_BASE_URL)
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');
}
